'use client';

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSession } from '@/lib/AuthContext';
import { useOrganization } from '@/lib/OrganizationContext';
import { LoadingPage } from '@/components/LoadingPage';
import { OrganizationSelection } from './OrganizationSelection';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOrganization?: boolean;
  requiredPermission?: string;
}

export const ProtectedRoute = ({
  children,
  requireOrganization = true,
  requiredPermission,
}: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, isLoading: sessionLoading } = useSession();
  const {
    currentOrganization,
    organizations,
    isLoading: orgLoading,
    hasPermission,
  } = useOrganization();

  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const returnPath = encodeURIComponent(location.pathname + location.search);

  useEffect(() => {
    if (!sessionLoading && !orgLoading && !session) {
      navigate(`/login?return=${returnPath}`, { replace: true });
    }
  }, [session, sessionLoading, orgLoading, navigate, returnPath]);

  if (!mounted || sessionLoading || orgLoading) {
    return <LoadingPage />;
  }

  if (!session) {
    return <LoadingPage />;
  }

  if (requireOrganization && organizations.length === 0) {
    return <OrganizationSelection />;
  }

  if (requireOrganization && !currentOrganization) {
    return <OrganizationSelection />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h1>
          <p className="text-gray-600">No tienes permisos para acceder a esta sección.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
